import {
  type InputEvent,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";

type AutoGrowTextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> & {
  maxRows?: number;
};

export function AutoGrowTextarea({
  className = "",
  maxRows = 8,
  onInput,
  value,
  ...props
}: AutoGrowTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;

    const computed = window.getComputedStyle(element);
    const lineHeight = pixelLineHeight(computed);
    const padding = numericCssValue(computed.paddingTop) + numericCssValue(computed.paddingBottom);
    const borders =
      numericCssValue(computed.borderTopWidth) + numericCssValue(computed.borderBottomWidth);
    const minimumHeight = numericCssValue(computed.minHeight) || lineHeight + padding + borders;
    const maximumHeight = lineHeight * maxRows + padding + borders;

    // 先回到自动高度，才能在删除文本时重新测量并向下收缩。
    element.style.height = "auto";
    const contentHeight = element.scrollHeight + borders;
    const nextHeight = Math.min(maximumHeight, Math.max(minimumHeight, contentHeight));
    element.style.height = `${nextHeight}px`;
    element.style.maxHeight = `${maximumHeight}px`;
    element.style.overflowY = contentHeight > maximumHeight ? "auto" : "hidden";
  }, [maxRows]);

  useLayoutEffect(() => {
    resize();
  }, [resize, value]);

  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  function handleInput(event: InputEvent<HTMLTextAreaElement>) {
    resize();
    onInput?.(event);
  }

  return (
    <textarea
      {...props}
      className={`auto-grow-textarea ${className}`.trim()}
      data-max-rows={maxRows}
      ref={textareaRef}
      rows={1}
      value={value}
      onInput={handleInput}
    />
  );
}

function pixelLineHeight(computed: CSSStyleDeclaration) {
  const parsedLineHeight = numericCssValue(computed.lineHeight);
  const fontSize = numericCssValue(computed.fontSize) || 16;
  if (!parsedLineHeight) return fontSize * 1.5;
  return parsedLineHeight < 4 ? parsedLineHeight * fontSize : parsedLineHeight;
}

function numericCssValue(value: string) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
